import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_OG_IMAGE,
  SITE_URL,
  canonicalUrl,
  findPageRoute,
  normalizePath,
} from '../lib/site-routes';

const SAME_AS = [
  'https://x.com/lykbl_official',
  'https://instagram.com/real_lykbl',
  'https://tiktok.com/@lykbl_official',
  'https://www.linkedin.com/company/lykbl/',
];

const HOME_JSON_LD = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: 'lykbl',
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/lykbl_logo.png`,
    sameAs: SAME_AS,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: 'lykbl',
    url: `${SITE_URL}/`,
    publisher: { '@id': `${SITE_URL}/#organization` },
  },
]);

/**
 * Site-wide SEO defaults for the current route: canonical, robots, og:url,
 * share image, Twitter card and (on `/`) Organization/WebSite JSON-LD.
 *
 * Rendered BEFORE <Routes> so any page-level <Helmet> that sets the same tag
 * (title, description, canonical, robots…) wins — react-helmet-async resolves
 * duplicates in favour of the instance rendered last, on the server and client
 * alike. Unknown paths get no canonical and `noindex`.
 */
export default function RouteSeo() {
  const { pathname } = useLocation();
  const path = normalizePath(pathname);
  const route = findPageRoute(path);
  const canonical = route ? canonicalUrl(path) : null;

  return (
    <Helmet>
      <meta name="robots" content={route?.indexable ? 'index, follow' : 'noindex, nofollow'} />
      {canonical && <link rel="canonical" href={canonical} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content="lykbl" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      {path === '/' && <script type="application/ld+json">{HOME_JSON_LD}</script>}
    </Helmet>
  );
}
